<template>
  <div class="h-screen overflow-hidden bg-gray-900 text-white flex">
    <!-- Sidebar di navigazione (collassabile) - mostrata solo se è selezionato un bot -->
    <BotEditorNavSidebar v-if="isBotEditor" :bot-id="currentBotId" />

    <!-- Wrapper for header + main -->
    <div class="flex-1 flex flex-col min-w-0">
      <!-- Header section -->
      <header v-if="!route.meta.fullHeight">
        <Header
          :is-in-bot-editor="isBotEditor"
          :bot-id="currentBotId"
          :bot-name="currentBotName"
          :locale="locale"
          @change-language="changeLanguage"
          @logout="logout"
        />
      </header>

      <!-- Main content area -->
      <main :class="['flex-1 flex flex-col', route.meta.fullHeight ? 'overflow-hidden' : 'overflow-y-auto px-8 pb-8']">
        <!-- Token Consumption Warning Banner -->
        <div v-if="shouldShowBanner && !route.meta.fullHeight" class="mb-6">
          <!-- First Threshold (Yellow) -->
          <div v-if="warningLevel === 'first'" class="p-4 bg-yellow-900/50 border border-yellow-600 rounded-lg">
            <div class="flex items-start gap-3">
              <span class="material-icons text-yellow-500 shrink-0">warning</span>
              <div class="flex-1">
                <p class="font-semibold text-yellow-400">{{ $t('auth.login.warningFirstTitle') }}</p>
                <p class="text-sm text-yellow-300 mt-1">{{ $t('auth.login.warningFirstMessage', { percent: tokenConsumptionPercent }) }}</p>
              </div>
              <!-- secondary, not warn: Aura's text-warn tint is {orange.400}, off-palette on the yellow banner -->
              <Button
                variant="text"
                severity="secondary"
                rounded
                size="small"
                class="shrink-0"
                v-tooltip.top="$t('common.close')"
                :aria-label="$t('common.close')"
                @click="dismissBanner"
              >
                <template #icon>
                  <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">close</span>
                </template>
              </Button>
            </div>
          </div>

          <!-- Second Threshold (Orange) -->
          <div v-if="warningLevel === 'second'" class="p-4 bg-orange-900/50 border border-orange-600 rounded-lg">
            <div class="flex items-start gap-3">
              <span class="material-icons text-orange-500 shrink-0">error</span>
              <div class="flex-1">
                <p class="font-semibold text-orange-400">{{ $t('auth.login.warningSecondTitle') }}</p>
                <p class="text-sm text-orange-300 mt-1">{{ $t('auth.login.warningSecondMessage', { percent: tokenConsumptionPercent }) }}</p>
              </div>
              <Button
                variant="text"
                severity="warn"
                rounded
                size="small"
                class="shrink-0"
                v-tooltip.top="$t('common.close')"
                :aria-label="$t('common.close')"
                @click="dismissBanner"
              >
                <template #icon>
                  <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">close</span>
                </template>
              </Button>
            </div>
          </div>
        </div>

        <!-- Le pagine (es. /app/bots) verranno renderizzate qui -->
        <router-view />
      </main>
    </div>

    <!-- Right chat sidebar — shown in bot editor, hidden on the Customize Chatroom page -->
    <BotEditorChatSidebar v-if="isChatSidebarVisible" :bot-id="currentBotId" />
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Header from '../components/layout/Header.vue';
import BotEditorNavSidebar from '../components/layout/BotEditorNavSidebar.vue';
import BotEditorChatSidebar from '../components/layout/BotEditorChatSidebar.vue';
import { useWorkspaceStore } from '../stores/workspaceStore';
import { useTokenWarningBanner } from '../composables/useTokenWarningBanner';
import { useAuth } from '@clerk/vue';
import api from '../services/api';

const router = useRouter();
const { locale } = useI18n();
const route = useRoute();
const workspaceStore = useWorkspaceStore();

// Token warning banner
const { warningLevel, tokenConsumptionPercent, shouldShowBanner, dismissBanner } = useTokenWarningBanner();

const isBotEditor = computed(() => {
  try {
    return route.path.includes('/bots/') && !!route.params.id;
  } catch (e) { return false; }
});
const currentBotId = computed(() => route.params.id || '');
const CHAT_SIDEBAR_HIDDEN_ROUTES = new Set(['BotCustomizeChat', 'BotDeploy']);
const isChatSidebarVisible = computed(() =>
  isBotEditor.value && !CHAT_SIDEBAR_HIDDEN_ROUTES.has(route.name)
);

// Make bot name reactive with ref instead of computed
const currentBotName = ref('Bot');

// Function to update bot name from localStorage
const updateBotNameFromStorage = () => {
  try {
    const b = localStorage.getItem('currentBot');
    if (b) {
      const parsed = JSON.parse(b);
      if (parsed && parsed.name) {
        currentBotName.value = parsed.name;
        return;
      }
    }
  } catch {}
  currentBotName.value = 'Bot';
};

function syncWorkspaceFromRoute(workspaceId) {
  if (!workspaceId || !workspaceStore.workspaces.length) return;
  if (workspaceStore.currentWorkspace?._id === workspaceId) return;
  const ws = workspaceStore.resolveWorkspace(workspaceId);
  if (ws) workspaceStore.switchWorkspace(ws);
}

// Initialize on mount and set up event listeners
onMounted(async () => {
  updateBotNameFromStorage();
  window.addEventListener('botNameChanged', updateBotNameFromStorage);
  if (!workspaceStore.workspaces.length) {
    // Catch here so a failing GET /workspaces isn't escalated to Vue's errorHandler (-> Rollbar);
    // the route sync and the invite badge below must still run.
    try {
      await workspaceStore.loadWorkspaces();
    } catch (err) {
      Console.error('[MainLayout] Failed to load workspaces:', err);
    }
  }
  syncWorkspaceFromRoute(route.params.workspaceId);
  await workspaceStore.loadPendingInviteCount();
});

// Keep currentWorkspace in sync when navigating between workspaces
watch(() => route.params.workspaceId, (newId) => {
  syncWorkspaceFromRoute(newId);
});

// Re-read bot name from localStorage when navigating between bots
watch(() => route.params.id, () => {
  updateBotNameFromStorage();
});

// Clean up listeners on unmount
onUnmounted(() => {
  window.removeEventListener('botNameChanged', updateBotNameFromStorage);
});

const changeLanguage = (lang) => {
  locale.value = lang;
  localStorage.setItem('preferredLanguage', lang);
  // Persiste la preferenza lato backend (lingua email + sync marketing). Best-effort.
  api.patch('/users/me', { locale: lang }).catch(() => {});
};

// User data from Clerk
const { signOut } = useAuth();

const logout = async () => {
  try {
    // Sign out from Clerk (this will clear Clerk session client-side)
    // Note: signOut is a Ref in Clerk Vue, so we need to call .value
    await signOut.value();
    // Use Vue Router to explicitly navigate to the login page. Hash router can be unreliable
    // when used via third-party redirectUrl options, so prefer router.push for explicit control.
    await router.push('/login');
  } catch (error) {
    Console.error('Logout error:', error);
    // Even if signOut fails, redirect the user to the login page as a fallback.
    router.push('/login');
  }

  // Clear sensitive localStorage keys while preserving UX preferences
  try {
    const sensitiveKeys = ['user', 'token', 'currentBot'];
    sensitiveKeys.forEach(k => localStorage.removeItem(k));

    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith('conversationId:') || key.startsWith('conversation:') || key === 'conversations' || key === 'messages') {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch (e) {
    /* ignore localStorage errors */
  }

  // Disconnect websocket if present
  try {
    const ws = await import('../composables/useWebSocket');
    if (ws && ws.disconnect) ws.disconnect();
  } catch (e) { /* ignore */ }

  // Redirect to login
  router.push('/login');
};
</script>

<style scoped>
/* Stile per il link attivo nella sidebar */
.router-link-exact-active {
  background-color: #0891b2; /* cyan-600 */
  color: white;
}
</style>
